import { CardActionArea } from '@mui/material';
import React, { useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DocumentType, PersonReferenceDTO } from '../../api/dto';
import { TaskPreciseDTO } from '../../api/task';
import { useDeviceType } from '../../hooks/useDeviceType';
import { translateTask, translateTaskType } from '../../lib/language';
import { getAssignmentLabel } from '../../lib/task';
import { DocumentDrawerContext } from '../documents/DocumentDrawerStructure';
import { TaskManageButton } from '../documents/TaskManageButton';
import { TaskCardLayout, TaskColor } from './TaskCardLayout';
import { ActionKind, DEFAULT_ACTION_KIND, TASK_CONFIGURATION } from './TaskTypeConfiguration';
import { useTaskCanBeCompleted } from './useTaskCanBeCompleted';
import { useTaskIsImportant } from './useTaskIsImportant';
import { useTasks } from './useTasks';

interface CustomCardActionAreaProps {
    onClick: () => void;
    readOnly: boolean;
    disabled: boolean;
    children: React.ReactNode;
}

// CardActionArea['disabled'] is not good because it also disables the content
const CustomCardActionArea: React.FC<CustomCardActionAreaProps> = ({ onClick, readOnly, disabled, children }) => {
    return !readOnly ? (
        <CardActionArea disabled={disabled} onClick={() => onClick()}>
            {children}
        </CardActionArea>
    ) : (
        <>{children}</>
    );
};

interface TaskCardProps {
    tasks: Pick<ReturnType<typeof useTasks>, 'currentTask' | 'setCurrentTask' | 'disabled'>;
    task: TaskPreciseDTO;
    isLoading: boolean;
    disabled: boolean;
    withUnsavedChangesPrompt: (f: () => void) => void;
    onTaskChange: () => void;
    unselectable: boolean;
    addIndividualAssignees: (args1: {
        task: TaskPreciseDTO;
    }) => (args2: { newAssignees: PersonReferenceDTO[]; comment: string | null }) => void;
    updateTimeout: (args1: { task: TaskPreciseDTO }) => (args2: { date: Date | null; isEscalation: boolean }) => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({
    tasks,
    task,
    isLoading,
    disabled,
    withUnsavedChangesPrompt,
    onTaskChange,
    unselectable,
    addIndividualAssignees,
    updateTimeout,
}) => {
    const { currentTask, setCurrentTask } = tasks;
    const intl = useIntl();
    const canBeCompleted = useTaskCanBeCompleted()(task);
    const { isMobile } = useDeviceType();
    const { closeDrawer, triggerActionButtonAnimation } = useContext(DocumentDrawerContext)!;
    const addDocumentType = (t: TaskPreciseDTO) => {
        // TODO
        // FIXME what is this???
        // when we decide that to show in the task card, for is37 change requests - and probably activity change
        // requests - we have to add information that this signature applies to the change request no the document
        if (t.documentType == DocumentType.IS37_CR) {
            return 'IS37 Change Request: ';
        } else return '';
    };
    const isCurrentTask = currentTask?.id === task.id;
    const isTaskImportant = useTaskIsImportant()(task);
    const handleCardClick = useCallback(
        () =>
            withUnsavedChangesPrompt(() => {
                onTaskChange();
                setCurrentTask(currentTask?.id !== task.id || !unselectable ? task : null);
                triggerActionButtonAnimation();
                if (isMobile) {
                    closeDrawer();
                }
            }),
        [
            task,
            currentTask,
            setCurrentTask,
            isCurrentTask,
            withUnsavedChangesPrompt,
            triggerActionButtonAnimation,
            isMobile,
            closeDrawer,
        ]
    );
    const taskColor = useMemo(() => {
        if (isCurrentTask) {
            // Selected task
            return TaskColor.ACTIVE_SELECTED;
        } else if (canBeCompleted) {
            // Selectable task (but not currently selected)
            return TaskColor.ACTIVE_UNSELECTED;
        } else if (task.taskActive) {
            // Unselectable active task
            return TaskColor.ACTIVE_UNSELECTABLE;
        } else {
            // Inactive task
            if (task.taskExecutor !== null) {
                // Performed
                const actionType = task.taskCompletionCode;
                const taskConfiguration = TASK_CONFIGURATION[task.type.type];
                let actionConfiguration;
                if (actionType !== null) {
                    actionConfiguration = taskConfiguration.all
                        ? taskConfiguration.actions[actionType]
                        : taskConfiguration.actions[task.documentType]?.[actionType];
                } else {
                }
                const configuration = Array.isArray(actionConfiguration) ? undefined : actionConfiguration; // TODO
                const colors = {
                    [ActionKind.MUST_DO]: TaskColor.COMPLETED_POSITIVE,
                    [ActionKind.CAN_DO_SOFT]: TaskColor.COMPLETED_NEUTRAL,
                    [ActionKind.CAN_DO_HARD]: TaskColor.COMPLETED_NEGATIVE,
                } satisfies Record<ActionKind, TaskColor>;
                return colors[configuration?.kind ?? DEFAULT_ACTION_KIND];
            } else {
                // Not performed
                return TaskColor.CANCELLED;
            }
        }
    }, [task, isCurrentTask, canBeCompleted]);
    const taskAssignment: [string, ...string[]] = (() => {
        const task1AssignmentLabel = translateTask(task, intl.locale).assignmentLabel;
        if (task1AssignmentLabel !== null) {
            const assignmentTargetLabel = task.assignmentTargetLabel;
            if (assignmentTargetLabel) {
                return [task1AssignmentLabel, assignmentTargetLabel];
            }
            return [task1AssignmentLabel];
        } else {
            // Includes the added assignees
            const sequenceIndices = task.assignees.flatMap((sequence, sequenceIndex) =>
                sequence.flat().some((assignee) => assignee.hasBeenNotified) ? [sequenceIndex] : []
            );
            const getLines = (selectedSequenceIndices: number[]) =>
                selectedSequenceIndices.flatMap(
                    (sequenceIndex) =>
                        (sequenceIndex < task.assignment.groups.length
                            ? task.assignment.groups[sequenceIndex].assignments.flatMap((assignment) =>
                                  getAssignmentLabel(assignment, intl)
                              )
                            : getAssignmentLabel(null, intl)
                        )
                            .map((o) => o.value)
                            .join('') // Join the different parts (but don't join the lines)
                );
            const lines = getLines(
                sequenceIndices.length > 0 ? sequenceIndices : task.assignment.groups.map((_, i) => i)
            ); // If there were no assignees, display all the groups
            return lines.length > 0 ? [lines[0], ...lines.slice(1) /* For TS */] : ['?'];
        }
    })();
    const notifiedAssignees = useMemo(
        () =>
            task.assignees
                .flat(2)
                .filter((assignee) => assignee.hasBeenNotified)
                .map((assignee) => assignee.person),
        [task]
    );
    return (
        <TaskCardLayout
            color={taskColor}
            icon={TASK_CONFIGURATION[task.type.type].icon}
            selected={isCurrentTask}
            readOnly={!canBeCompleted}
            disabled={disabled}
            dashed={!isCurrentTask && !isTaskImportant}
            type={addDocumentType(task) + translateTaskType(task.type, intl.locale).typeName}
            role={taskAssignment}
            status={
                task.taskActive
                    ? {
                          active: true,
                          assignees: notifiedAssignees,
                      }
                    : {
                          active: false,
                          assignees: notifiedAssignees,
                          executor: task.taskExecutor,
                          date: new Date(task.closedDate!),
                      }
            }
            instruction={
                task.taskActive && canBeCompleted ? TASK_CONFIGURATION[task.type.type].instruction(intl) : undefined
            }
            action={
                <TaskManageButton
                    task={task}
                    mutateAddAssignees={addIndividualAssignees({ task })}
                    mutateTimeout={updateTimeout({ task })}
                    disabled={isLoading}
                    moreActionsDisabled={tasks.disabled || disabled}
                    readOnly={!canBeCompleted}
                />
            }
            onCardClick={handleCardClick}
        />
    );
};
